import type { FormEventHandler } from 'react'
import { useCallback, useEffect, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import Group from 'components/utils/Group'
import TextArea from 'components/Common/Form/Input/TextArea'
import { formToObject } from 'lib/forms/formToObject'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import { connect } from 'react-redux'
import TextInput from 'components/Common/Form/Input/TextInput'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import AgentCallbackRequestDateTimeInput from '../RequestAgentCallbackDateTimeInput'
import { parseSelectedNumberOfTravellersToFormText, parseSelectedTravelDatesToFormText } from '../RequestAgentCallbackUtils'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import AccountAccessModalContent from 'components/Account/AccountAccess/AccountAccessModalContent'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import { createRequestAgentCallback } from '../CreateRequestAgentCallback'
import { RequestAgentCallbackConfirmationModal } from './RequestAgentCallbackConfirmationModal'
import { RequestAgentCallbackErrorModal } from './RequestAgentCallbackErrorModal'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { RequestAgentCallbackWarningModal } from './RequestAgentCallbackWarningModal'
import * as Analytics from 'analytics/analytics'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'

const callbackTypeToAnalyticsSubject: Record<App.GenesysAgentCallbackType, string> = {
  SALES: 'request_agent_callback_sales',
  SUPPORT: 'request_agent_callback_support',
  HOTELS_AND_VILLAS: '',
  LESA: '',
  TOURS: '',
  FLIGHTS: '',
}

export interface RequestAgentCallbackResult {
  firstName?: string
  lastName?: string
  type: App.GenesysAgentCallbackType
  email?: string
  phone: { phone: string, prefix: string }
  destination?: string
  travelDates?: string
  message: string
  numberOfTravellers?: string
  callbackDate: Date
  callbackTime: string
  timezone: string
}

export interface MappedRequestAgentCallbackResult {
  chatId: string | undefined
  customerId: string
  customerPhone: string
  type: App.GenesysAgentCallbackType
  isImmediateCallback: boolean
  updatePhoneNumber: boolean
  customerHasRecentlyScheduledCallback: boolean
  customer: {
    firstName: string
    lastName: string
    email: string
    phone: string
    message?: string
    destination?: string
    numberOfTravellers?: string
    travelDates?: string
    countryCode?: string
    callbackDate?: Date
    callbackTime?: string
    timezone?: string
  }
}

interface MappedStateProps {
  loggedIn: boolean
  account: App.AuthAccount
  filters?: App.OfferListFilters
}

interface Props {
  type: App.GenesysAgentCallbackType
  chatId?: string
  isLESATouchpoint?: boolean
  filters?: App.OfferListFilters
  location?: string
  dismissable?: boolean
  initialMode?: App.UiAccountModalMode
  resetPasswordToken?: string
  hotelOfferDetails?: App.AccountModalOfferDetails
  defaultEmail?: string
  callbackPath?: string
}

function RequestAgentCallbackModal(props: MappedStateProps & Props) {
  const {
    initialMode = 'requestAgentCallbackLogin',
    resetPasswordToken,
    hotelOfferDetails,
    dismissable,
    defaultEmail,
    callbackPath,
    loggedIn,
    account,
    type,
    chatId,
    isLESATouchpoint,
    filters,
    location,
  } = props

  const intl = useIntl()

  useEffect(() => {
    Analytics.trackClientEvent({
      action: 'request_agent_callback_form_displayed',
      subject: callbackTypeToAnalyticsSubject[type],
      category: AnalyticsPage.requestAgentCallback,
      type: 'interaction',
    })
  }, [type])

  const [mode, setMode] = useState<App.UiAccountModalMode>(initialMode)
  const loading = useAppSelector(state => state.auth.processing)

  const modalElement = useModalElementContext<MappedRequestAgentCallbackResult | undefined>()

  const [numberOfTravellers, setNumberOfTravellers] = useState(parseSelectedNumberOfTravellersToFormText(filters ?? {}, intl) ?? '')
  const [travelDates, setTravelDates] = useState(parseSelectedTravelDatesToFormText(filters ?? {}, intl) ?? intl.formatMessage({
    id: 'D5x2F4IzxftPMWpB2Fa6',
    defaultMessage: 'Anytime',
    description: 'Default travel dates value for callback request form',
  }))
  const [results, setResults] = useState<MappedRequestAgentCallbackResult | null>(null)
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false)
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false)
  const [showWarningModal, setShowWarningModal] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  useEffect(() => {
    if (filters) {
      setNumberOfTravellers(parseSelectedNumberOfTravellersToFormText(filters, intl) || '')
      setTravelDates(parseSelectedTravelDatesToFormText(filters, intl) || intl.formatMessage({
        id: 'scQsJcKybt3bXhFtITbk',
        defaultMessage: 'Anytime',
        description: 'Default travel dates fallback for callback request form',
      }))
    }
  }, [filters, intl])

  const handleNumberOfTravellersChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setNumberOfTravellers(e.target.value)
  }

  const handleTravelDatesChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setTravelDates(e.target.value)
  }

  const MAX_LENGTH = 100
  const MAX_LENGTH_COMMENTS = 500

  const showCallbackForm: boolean = !showConfirmationModal && !showWarningModal && !showErrorModal

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(async(e) => {
    e.preventDefault()
    setIsSubmitting(true)
    const requestAgentCallbackFormData = formToObject<RequestAgentCallbackResult>(e.currentTarget)

    try {
      const results: MappedRequestAgentCallbackResult = await createRequestAgentCallback(requestAgentCallbackFormData, account, chatId)

      setResults(results)
      if (isLESATouchpoint) {
        modalElement.resolve(results)
      }
      else if (results.customerHasRecentlyScheduledCallback) {
        setShowWarningModal(true)
      }
      else {
        setShowConfirmationModal(true)
      }
    } catch {
      setShowErrorModal(true)
    }
    finally {
      setIsSubmitting(false)
    }
  }, [account, chatId, isLESATouchpoint, modalElement])

  return (
    <ModalBase>
      <LoadingIndicator visible={loading} floating />
      {!loggedIn &&
        <AccountAccessModalContent
          mode={mode}
          defaultEmail={defaultEmail}
          onModeChange={setMode}
          resetPasswordToken={resetPasswordToken}
          hotelOfferDetails={hotelOfferDetails}
          callbackPath={callbackPath}
          dismissable={dismissable}
        />}
      {loggedIn && showCallbackForm &&
        <form onSubmit={handleSubmit}>
          <ModalHeader title={intl.formatMessage({
            id: 'MdjLVExO_9sMGVOXIif4',
            defaultMessage: 'Request a callback',
            description: 'Title for the request agent callback modal in the agent portal',
          })}
          />
          <ModalBody>
            <ModalContent>
              <VerticalSpacer gap={20}>
                {(type === 'HOTELS_AND_VILLAS') && (
                  <BodyTextBlock variant="large">
                    {intl.formatMessage(
                      {
                        id: 'xij7p65cctxiIbO_l4QY',
                        defaultMessage: 'Hi {givenName}, you are entitled to a personalised concierge call to assist with your booking. Share your contact details, and our 24/7 concierge team will get back to you as soon as possible.',
                        description: 'Introduction text for hotels and villas callback request with personalized greeting',
                      },
                      { givenName: account.givenName },
                    )}
                  </BodyTextBlock>
                )}

                {['FLIGHTS', 'LESA', 'SALES', 'SUPPORT', 'TOURS'].includes(type) && (
                  <BodyTextBlock variant="large">
                    {intl.formatMessage(
                      {
                        id: '0Oq4gs-v2jhU8wE5bxS_',
                        defaultMessage: 'Share your details with us, and our {teamType} team will get back to you as soon as possible.',
                        description: 'Introduction text for callback request with team type',
                      },
                      { teamType: type === 'SALES' ? 'sales' : type === 'TOURS' ? 'tours concierge' : 'support' },
                    )}
                  </BodyTextBlock>
                )}
                <Group direction="vertical" gap={8}>
                  {['FLIGHTS', 'LESA', 'SALES', 'SUPPORT'].includes(type) && (
                    <Group direction="horizontal" gap={8} horizontalAlign="stretch">
                      <TextInput
                        name="firstName"
                        defaultValue={account.givenName}
                        invalidErrorMessage={intl.formatMessage({
                          id: 'yCRVAmvt4K0nXQrSupI1',
                          defaultMessage: 'Invalid characters. Please use only letters and spaces',
                          description: 'Validation error for first name with invalid characters',
                        })}
                        label={intl.formatMessage({
                          id: 'vn47O_iQED4cwD1Wt3o2',
                          defaultMessage: 'First name *',
                          description: 'Form field label for first name input in the callback form',
                        })}
                        maxLength={MAX_LENGTH}
                        minLength={2}
                        pattern="^[A-Za-z ]+$"
                        placeholder={intl.formatMessage({
                          id: 'JUCDQ7Rz9HdXILLczwTI',
                          defaultMessage: 'Your first name',
                          description: 'Input placeholder text for first name input in the callback form',
                        })}
                        required
                        requiredErrorMessage={intl.formatMessage({
                          id: 'qzZDCdNdLn_SxWShwwrF',
                          defaultMessage: 'First name is required',
                          description: 'Required validation error for first name input on the form',
                        })}
                      />
                      <TextInput
                        name="lastName"
                        defaultValue={account.surname}
                        invalidErrorMessage={intl.formatMessage({
                          id: 'cxZ9eyFh7Xs3tND4tEi1',
                          defaultMessage: 'Invalid characters. Please use only letters and spaces',
                          description: 'Validation error for last name with invalid characters',
                        })}
                        label={intl.formatMessage({
                          id: 'fop5bbvh2FBNn0TgXeXR',
                          defaultMessage: 'Last name *',
                          description: 'Form field label for last name input in the callback form',
                        })}
                        maxLength={MAX_LENGTH}
                        minLength={2}
                        pattern="^[A-Za-z ]+$"
                        placeholder={intl.formatMessage({
                          id: 'Ml_IXB99D3e5AQ9oNrQY',
                          defaultMessage: 'Your last name',
                          description: 'Input placeholder text for last name input in the callback form',
                        })}
                        required
                        requiredErrorMessage={intl.formatMessage({
                          id: 'zllXrM8MQFzGeYryQI1_',
                          defaultMessage: 'Last name is required',
                          description: 'Required validation error for last name input on the form',
                        })}
                      />
                    </Group>
                  )}
                  <AgentCallbackRequestDateTimeInput type={type} />
                  <PhoneInput
                    defaultPhoneNumber={account.phone}
                    defaultPhonePrefix={account.phonePrefix}
                    label={intl.formatMessage({
                      id: 'go8BNgqVpuBrmLFRpj2q',
                      defaultMessage: 'Mobile number',
                      description: 'Label for mobile phone number input in callback form',
                    })}
                    name="phone"
                    placeholder={intl.formatMessage({
                      id: '0j1EJRqxK_ms_TE4U1-h',
                      defaultMessage: 'e.g. 412 345 678',
                      description: 'Placeholder example for mobile phone number input on the form',
                    })}
                    required
                    requiredErrorMessage={intl.formatMessage({
                      id: 'CsIQSKwulZJojENZf0Iz',
                      defaultMessage: 'Mobile number is required',
                      description: 'Required validation error for mobile number input on the form',
                    })}
                  />
                  {(type === 'HOTELS_AND_VILLAS' || type === 'TOURS') && (
                    <>
                      <TextInput
                        name="destination"
                        defaultValue={location}
                        invalidErrorMessage={intl.formatMessage({
                          id: 'IBZFlRmkSA67UeS-1V9w',
                          defaultMessage: 'Invalid characters. Please use only letters and spaces',
                          description: 'Validation error for destination with invalid characters',
                        })}
                        label={intl.formatMessage({
                          id: 'c-zLt9rk7BGZ6CLp2Kpw',
                          defaultMessage: 'Where do you plan to go?',
                          description: 'Form field label for the destination input in the callback form',
                        })}
                        maxLength={MAX_LENGTH}
                        minLength={2}
                        autoComplete="off"
                        pattern="^[A-Za-z ]+$"
                        placeholder={intl.formatMessage({
                          id: 'YFn879RvRoMoKRZOqPTd',
                          defaultMessage: 'The destination you would like to travel to',
                          description: 'Placeholder text for the travel destination input in the agent callback request form',
                        })}
                        required
                        requiredErrorMessage={intl.formatMessage({
                          id: 'dILRXjOqc9lwFtZjQH_T',
                          defaultMessage: 'Destination is required',
                          description: 'Required validation error for destination input on the form',
                        })}
                      />
                      {type === 'TOURS' &&
                        <TextInput
                          name="travelDates"
                          label={intl.formatMessage({
                            id: 'RHhO_XiAfWiGIosuQzFe',
                            defaultMessage: 'When are you looking to travel?',
                            description: 'Label for travel dates input in tours callback form',
                          })}
                          maxLength={MAX_LENGTH}
                          minLength={2}
                          autoComplete="off"
                          placeholder={intl.formatMessage({
                            id: 'PECXzBLKfe8-Qvf16NEL',
                            defaultMessage: 'The dates you would like to travel on',
                            description: 'Placeholder for travel dates input in tours callback form',
                          })}
                          required
                          requiredErrorMessage={intl.formatMessage({
                            id: 'GBlYfTbrgbqa1dnmo7Kv',
                            defaultMessage: 'Dates are required',
                            description: 'Required validation error for travel dates input on the form',
                          })}
                        />}
                      {type === 'HOTELS_AND_VILLAS' &&
                        <TextInput
                          name="travelDates"
                          value={travelDates}
                          onChange={handleTravelDatesChange}
                          label={intl.formatMessage({
                            id: 'QGq5zxY_Iq_LEYoVbQ3R',
                            defaultMessage: 'When are you looking to travel?',
                            description: 'Label for travel dates input in hotels callback form',
                          })}
                          maxLength={MAX_LENGTH}
                          minLength={2}
                          autoComplete="off"
                          placeholder={intl.formatMessage({
                            id: 'FiP84LqGi8gc11M-5yQk',
                            defaultMessage: 'The dates you would like to travel on',
                            description: 'Placeholder for travel dates input in hotels callback form',
                          })}
                          required
                          startIcon={<LineCalendarIcon />}
                          requiredErrorMessage={intl.formatMessage({
                            id: 'C3z-mVW7BTqJy-FEzXI2',
                            defaultMessage: 'Dates are required',
                            description: 'Required validation error for travel dates in hotels callback form',
                          })}
                        />}
                      {type === 'HOTELS_AND_VILLAS' && (
                        <TextInput
                          name="numberOfTravellers"
                          value={numberOfTravellers}
                          onChange={handleNumberOfTravellersChange}
                          label={intl.formatMessage({
                            id: 'bHCcwn7NF1jmbos5WSHO',
                            defaultMessage: 'How many people will you be travelling with?',
                            description: 'Label for number of travellers input in callback form',
                          })}
                          maxLength={MAX_LENGTH}
                          minLength={1}
                          autoComplete="off"
                          placeholder={intl.formatMessage({
                            id: 'ussxbY0uMzArbDjXqica',
                            defaultMessage: 'The number of people you will be travelling with',
                            description: 'Placeholder for number of travellers input in the traveller form',
                          })}
                          required
                          requiredErrorMessage={intl.formatMessage({
                            id: 'C-AgyBqBNCBjB0lu4roK',
                            defaultMessage: 'Number of people you are travelling with is required',
                            description: 'Required validation error for number of travellers input',
                          })}
                        />
                      )}
                    </>
                  )}
                  {(type === 'LESA' || type === 'SUPPORT' || type === 'SALES') && (
                    <>
                      <TextInput
                        invalidErrorMessage={intl.formatMessage({
                          id: 'opDchOAfkRQxGRCFQ7fy',
                          defaultMessage: 'Invalid email address',
                          description: 'Validation error for invalid email format in callback form',
                        })}
                        defaultValue={account.email}
                        label={intl.formatMessage({
                          id: '6JsUKw148rMzAYASe1WV',
                          defaultMessage: 'Email address *',
                          description: 'Form field label for the email input in the callback form',
                        })}
                        maxLength={MAX_LENGTH}
                        name="email"
                        placeholder={intl.formatMessage({
                          id: 'Ih1MA-wDmpGklkGINLVI',
                          defaultMessage: 'Enter your business email',
                          description: 'Placeholder for email input in callback form when field is empty',
                        })}
                        required
                        requiredErrorMessage={intl.formatMessage({
                          id: 'RCjQqjdeTdYIAbDYDgbv',
                          defaultMessage: 'Email address is required',
                          description: 'Required validation error for email input when an issue occurs',
                        })}
                        type="email"
                      />
                      <TextArea
                        label={intl.formatMessage({
                          id: 'H82x5vzgcR4zPUKHY0u-',
                          defaultMessage: 'Message',
                          description: 'Label for message textarea in support/sales callback form',
                        })}
                        maxLength={MAX_LENGTH_COMMENTS}
                        name="message"
                        placeholder={intl.formatMessage({
                          id: 'TLD4qrB48tjfoRcAwKFP',
                          defaultMessage: 'e.g. I have a payment issue',
                          description: 'Placeholder for message textarea in support/sales callback form',
                        })}
                      />
                    </>
                  )}
                  {(type === 'HOTELS_AND_VILLAS' || type === 'TOURS') && (
                    <TextArea
                      label={intl.formatMessage({
                        id: 'VDNrD6_RoHsgP4jjx9NC',
                        defaultMessage: 'Any other questions or comments?',
                        description: 'Label for message textarea in hotels/tours callback form',
                      })}
                      maxLength={MAX_LENGTH_COMMENTS}
                      name="message"
                      placeholder={intl.formatMessage({
                        id: 'oaDvpbb7ue1PVnTouJ5n',
                        defaultMessage: 'e.g. Do I need a vaccine?',
                        description: 'Placeholder for message textarea in hotels/tours callback form',
                      })}
                    />
                  )}

                  {type === 'FLIGHTS' && (
                    <TextArea
                      label={intl.formatMessage({
                        id: 'apNP6219d9bXmw9lXQqs',
                        defaultMessage: 'What would you like to ask?',
                        description: 'Label for message textarea in flights callback form',
                      })}
                      maxLength={MAX_LENGTH_COMMENTS}
                      name="message"
                      placeholder={intl.formatMessage({
                        id: 'JHP3lGCjdiEJy_26jxkB',
                        defaultMessage: 'e.g. Can I change my booking date?',
                        description: 'Placeholder for message textarea in flights callback form',
                      })}
                    />
                  )}
                  <HiddenInput
                    name="type"
                    value={type}
                  />
                </Group>
              </VerticalSpacer>
            </ModalContent>
          </ModalBody>
          {isSubmitting && <LoadingIndicator floating />}
          <ModalFooter
            primaryActionProps={{
              children: intl.formatMessage({
                id: '4PlRTYD7woo__6dNbhtu',
                defaultMessage: 'Submit Request',
                description: 'Submit button text for callback request form in a modal dialog',
              }),
              type: 'submit',
              disabled: isSubmitting,
            }}
          />
        </form>}
      {showConfirmationModal && results && <RequestAgentCallbackConfirmationModal customer={results.customer} />}
      {showWarningModal && <RequestAgentCallbackWarningModal />}
      {showErrorModal && <RequestAgentCallbackErrorModal />}
    </ModalBase>
  )
}

export default connect<MappedStateProps, undefined, Partial<Props>, App.State>(
  (state) => ({
    loggedIn: isLoggedInSelector(state),
    account: state.auth.account,
  }),
)(RequestAgentCallbackModal)
